import React, { useEffect } from 'react'
import Wordmark from '@/components/layout/Wordmark'
import { Phone, MessageSquare, Brain, BarChart3 } from 'lucide-react'

export const EASE_OUT_STRONG = 'cubic-bezier(0.23, 1, 0.32, 1)'

const brandValueProps = [
  { icon: Phone, title: 'AI voice agents that answer 24/7', detail: 'Never miss a call — inbound and outbound handled' },
  { icon: MessageSquare, title: 'Smart chat on your website', detail: 'Engage visitors the moment they land' },
  { icon: Brain, title: 'Knowledge base built from your site', detail: 'Firm-specific answers, not generic AI' },
  { icon: BarChart3, title: 'Every interaction tracked', detail: 'Calls, chats, leads — all in one place' },
]

/**
 * Shared split-screen shell used by every auth-style page (login, reset
 * password, verify email, accept invite). Left panel is the branded hero
 * with value props; right panel holds whatever the page provides as
 * children. Background stays on the dark royal gradient regardless of
 * which page mounts.
 *
 * IMPORTANT: module-scoped component so React doesn't remount the subtree
 * on every parent render. See feedback_react_patterns.md.
 */
export function AuthShell({
  headline,
  sub,
  children,
}: {
  headline: React.ReactNode
  sub: React.ReactNode
  children: React.ReactNode
}) {
  // Match <html> AND <body> background to the auth gradient so iOS
  // rubber-band overscroll doesn't flash the light app `bg-background`
  // above or below the page on mobile. Same pattern OnboardingPage and
  // RedeemPage used inline; centralizing here so every AuthShell consumer
  // (login, redeem, accept-invite, affiliate apply, etc.) gets it.
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtml = html.style.backgroundColor
    const prevBody = body.style.backgroundColor
    html.style.backgroundColor = '#0d0a1f'
    body.style.backgroundColor = '#0d0a1f'
    return () => {
      html.style.backgroundColor = prevHtml
      body.style.backgroundColor = prevBody
    }
  }, [])

  return (
    <div
      className="min-h-[100dvh] text-white"
      style={{
        background:
          'radial-gradient(ellipse at top left, rgba(77,113,253,0.18) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(25,36,128,0.22) 0%, transparent 50%), linear-gradient(180deg, #0d0a1f 0%, #12103a 100%)',
      }}
    >
      <div className="lg:grid lg:grid-cols-[minmax(320px,420px)_1fr] min-h-[100dvh]">
        {/* ===== LEFT BRAND PANEL (desktop only) ===== */}
        <aside className="hidden lg:flex lg:flex-col lg:justify-between lg:h-screen lg:sticky lg:top-0 px-10 py-10 border-r border-white/5 relative overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-20 -left-20 h-80 w-80 rounded-full blur-[120px] opacity-60"
            style={{ background: 'radial-gradient(circle, rgba(77,113,253,0.45), transparent 70%)' }}
          />
          <div className="relative">
            <Wordmark size="lg" variant="light" className="mb-10" />
            <h1
              className="text-3xl xl:text-[2.5rem] font-heading font-semibold text-white tracking-tight leading-[1.05] mb-4 opacity-0 animate-fade-in-up"
              style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
            >
              {headline}
            </h1>
            <p
              className="text-white/60 text-[15px] leading-relaxed mb-9 max-w-sm opacity-0 animate-fade-in-up"
              style={{ animationDelay: '160ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
            >
              {sub}
            </p>
            <ul className="space-y-4">
              {brandValueProps.map((prop, idx) => (
                <li
                  key={prop.title}
                  className="flex items-start gap-3 opacity-0 animate-fade-in-up"
                  style={{
                    animationDelay: `${240 + idx * 70}ms`,
                    animationFillMode: 'forwards',
                    animationTimingFunction: EASE_OUT_STRONG,
                  }}
                >
                  <div className="h-9 w-9 rounded-xl bg-brand-royal/20 border border-brand-royal/30 flex items-center justify-center shrink-0 shadow-md shadow-brand-royal/10">
                    <prop.icon className="h-4 w-4 text-brand-periwinkle" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white leading-snug">{prop.title}</p>
                    <p className="text-xs text-white/50 mt-0.5 leading-snug">{prop.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <p className="relative text-xs text-white/35 leading-relaxed">
            Trusted by law firms for front-desk calls, lead capture, and client intake — powered by AI trained on your practice.
          </p>
        </aside>

        {/* ===== RIGHT CONTENT ===== */}
        <main className="flex items-start lg:items-center justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-12 xl:px-20 lg:py-12 min-h-[100dvh]">
          <div className="w-full max-w-md">
            <div className="lg:hidden text-center mb-8">
              <Wordmark size="lg" variant="light" className="justify-center mb-5" />
            </div>
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
